import type {
  AddFeatureRequestInput,
  FeatureRequest,
  FeatureRequestStatus,
  FeatureVote,
} from '@/pages/FeatureFeedback/types';

import { baseApiClient as api } from './baseService';

const FEATURE_FEEDBACK_TAG = 'feature-feedback' as const;

interface FeatureVoteApi {
  voter_id: string;
  voter_name: string;
  created_at: string;
}

interface FeatureRequestApi {
  id: string;
  title: string;
  description: string;
  category: FeatureRequest['category'];
  status: FeatureRequestStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
  created_by_id: string | null;
  votes: FeatureVoteApi[];
}

interface FeatureRequestListApi {
  requests: FeatureRequestApi[];
}

function mapFeatureVote(payload: FeatureVoteApi): FeatureVote {
  return {
    voterId: payload.voter_id,
    voterName: payload.voter_name,
    createdAt: payload.created_at,
  };
}

function mapFeatureRequest(payload: FeatureRequestApi): FeatureRequest {
  return {
    id: payload.id,
    title: payload.title,
    description: payload.description,
    category: payload.category,
    status: payload.status,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    createdBy: payload.created_by,
    createdById: payload.created_by_id,
    votes: payload.votes.map(mapFeatureVote),
  };
}

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes: [FEATURE_FEEDBACK_TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getFeatureRequests: build.query<FeatureRequest[], void>({
        query: () => ({
          url: '/api/feature-requests/',
        }),
        transformResponse: (response: FeatureRequestListApi) =>
          response.requests.map(mapFeatureRequest),
        providesTags: (result) => {
          if (!result) {
            return [{ type: FEATURE_FEEDBACK_TAG, id: 'LIST' }];
          }
          return [
            ...result.map((request) => ({
              type: FEATURE_FEEDBACK_TAG,
              id: request.id,
            })),
            { type: FEATURE_FEEDBACK_TAG, id: 'LIST' },
          ];
        },
      }),
      createFeatureRequest: build.mutation<FeatureRequest, AddFeatureRequestInput>({
        query: (body) => ({
          url: '/api/feature-requests/',
          method: 'POST',
          body,
        }),
        transformResponse: (response: FeatureRequestApi) =>
          mapFeatureRequest(response),
        invalidatesTags: [{ type: FEATURE_FEEDBACK_TAG, id: 'LIST' }],
      }),
      toggleFeatureRequestVote: build.mutation<FeatureRequest, { requestId: string }>({
        query: ({ requestId }) => ({
          url: `/api/feature-requests/${requestId}/vote/`,
          method: 'POST',
        }),
        transformResponse: (response: FeatureRequestApi) =>
          mapFeatureRequest(response),
        invalidatesTags: (_result, _error, arg) => [
          { type: FEATURE_FEEDBACK_TAG, id: arg.requestId },
          { type: FEATURE_FEEDBACK_TAG, id: 'LIST' },
        ],
      }),
      updateFeatureRequestStatus: build.mutation<
        FeatureRequest,
        { requestId: string; status: FeatureRequestStatus }
      >({
        query: ({ requestId, status }) => ({
          url: `/api/feature-requests/${requestId}/status/`,
          method: 'PATCH',
          body: { status },
        }),
        transformResponse: (response: FeatureRequestApi) =>
          mapFeatureRequest(response),
        invalidatesTags: (_result, _error, arg) => [
          { type: FEATURE_FEEDBACK_TAG, id: arg.requestId },
          { type: FEATURE_FEEDBACK_TAG, id: 'LIST' },
        ],
      }),
      updateFeatureRequest: build.mutation<
        FeatureRequest,
        {
          requestId: string;
          title: string;
          description: string;
        }
      >({
        query: ({ requestId, title, description }) => ({
          url: `/api/feature-requests/${requestId}/`,
          method: 'PATCH',
          body: { title, description },
        }),
        transformResponse: (response: FeatureRequestApi) =>
          mapFeatureRequest(response),
        invalidatesTags: (_result, _error, arg) => [
          { type: FEATURE_FEEDBACK_TAG, id: arg.requestId },
          { type: FEATURE_FEEDBACK_TAG, id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useCreateFeatureRequestMutation,
  useGetFeatureRequestsQuery,
  useToggleFeatureRequestVoteMutation,
  useUpdateFeatureRequestMutation,
  useUpdateFeatureRequestStatusMutation,
} = injectedRtkApi;
